import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Epic Splash Preloader for CarCortex-AI
 * A visually stunning intro that "flasht" the user
 */
export const SplashPreloader = ({ onComplete, duration = 2000 }) => {
  const [phase, setPhase] = useState(0); // 0: logo, 1: text, 2: expand, 3: done
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Phase timing
    const timers = [
      setTimeout(() => setPhase(1), 400),   // Show text
      setTimeout(() => setPhase(2), 1200),  // Start expand
      setTimeout(() => setPhase(3), 1600),  // Fade out
      setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, duration),
    ];

    return () => timers.forEach(clearTimeout);
  }, [duration, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
          style={{ backgroundColor: '#0B0F14' }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        >
          {/* Animated Background Grid */}
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `
                  linear-gradient(rgba(124, 212, 253, 0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(124, 212, 253, 0.1) 1px, transparent 1px)
                `,
                backgroundSize: '50px 50px',
                animation: 'gridMove 20s linear infinite',
              }}
            />
          </div>

          {/* Particle Effects */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 rounded-full"
                style={{
                  background: i % 2 === 0 ? '#7CD4FD' : '#A78BFA',
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -100, 0],
                  x: [0, Math.random() * 50 - 25, 0],
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>

          {/* Central Glow */}
          <motion.div
            className="absolute"
            animate={{
              scale: phase >= 2 ? [1, 15] : [1, 1.2, 1],
              opacity: phase >= 2 ? [0.6, 0] : [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: phase >= 2 ? 0.6 : 2,
              repeat: phase >= 2 ? 0 : Infinity,
              ease: 'easeOut',
            }}
            style={{
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(124, 212, 253, 0.4) 0%, rgba(167, 139, 250, 0.2) 40%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />

          {/* Secondary Glow Ring */}
          <motion.div
            className="absolute rounded-full"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.2, 0.4, 0.2],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            }}
            style={{
              width: '300px',
              height: '300px',
              border: '2px solid rgba(124, 212, 253, 0.3)',
              boxShadow: '0 0 60px rgba(124, 212, 253, 0.3), inset 0 0 60px rgba(124, 212, 253, 0.1)',
            }}
          />

          {/* Logo Container */}
          <div className="relative z-10 flex flex-col items-center">
            {/* Animated Logo Mark */}
            <motion.div
              className="relative"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ 
                scale: phase >= 2 ? 1.2 : 1, 
                rotate: 0,
              }}
              transition={{ 
                type: 'spring', 
                stiffness: 200, 
                damping: 15,
                duration: 0.8,
              }}
            >
              {/* Outer Ring */}
              <motion.div
                className="absolute -inset-4 rounded-full"
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: 'linear',
                }}
                style={{
                  background: 'conic-gradient(from 0deg, transparent, #7CD4FD, transparent, #A78BFA, transparent)',
                  opacity: 0.6,
                }}
              />
              
              {/* Logo Background */}
              <motion.div
                className="relative w-24 h-24 rounded-2xl flex items-center justify-center overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #7CD4FD 0%, #1EA0E6 50%, #A78BFA 100%)',
                  boxShadow: '0 0 60px rgba(124, 212, 253, 0.6), 0 0 120px rgba(167, 139, 250, 0.4)',
                }}
                animate={{
                  boxShadow: [
                    '0 0 60px rgba(124, 212, 253, 0.6), 0 0 120px rgba(167, 139, 250, 0.4)',
                    '0 0 80px rgba(124, 212, 253, 0.8), 0 0 160px rgba(167, 139, 250, 0.6)',
                    '0 0 60px rgba(124, 212, 253, 0.6), 0 0 120px rgba(167, 139, 250, 0.4)',
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                {/* Inner Shine */}
                <motion.div
                  className="absolute inset-0"
                  animate={{
                    background: [
                      'linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.3) 50%, transparent 60%)',
                      'linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.3) 50%, transparent 60%)',
                    ],
                    backgroundPosition: ['-200% 0', '200% 0'],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 1,
                  }}
                />
                
                {/* Brain/Car Icon */}
                <svg 
                  viewBox="0 0 24 24" 
                  className="w-12 h-12 text-white relative z-10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  {/* Simplified car with brain-like circuit pattern */}
                  <motion.path
                    d="M5 17h14M7 13h10M6 9l2-4h8l2 4"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  />
                  <motion.circle
                    cx="7" cy="17" r="2"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.6 }}
                  />
                  <motion.circle
                    cx="17" cy="17" r="2"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.7 }}
                  />
                  {/* AI/Brain nodes */}
                  <motion.circle
                    cx="12" cy="8" r="1"
                    fill="currentColor"
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.5, 1] }}
                    transition={{ duration: 0.4, delay: 0.8 }}
                  />
                  <motion.path
                    d="M12 8v3M10 10h4"
                    strokeWidth="1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.9 }}
                  />
                </svg>
              </motion.div>
            </motion.div>

            {/* Brand Text */}
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ 
                opacity: phase >= 1 ? 1 : 0, 
                y: phase >= 1 ? 0 : 20,
              }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              {/* Main Title with Letter Animation */}
              <div className="flex items-center justify-center gap-0.5 text-4xl font-bold">
                {'CarCortex'.split('').map((letter, i) => (
                  <motion.span
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: phase >= 1 ? 1 : 0, y: phase >= 1 ? 0 : 20 }}
                    transition={{ duration: 0.3, delay: 0.1 + i * 0.05 }}
                    className="inline-block"
                    style={{
                      background: 'linear-gradient(135deg, #7CD4FD 0%, #FFFFFF 50%, #A78BFA 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      textShadow: '0 0 40px rgba(124, 212, 253, 0.5)',
                    }}
                  >
                    {letter}
                  </motion.span>
                ))}
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: phase >= 1 ? 1 : 0, scale: phase >= 1 ? 1 : 0 }}
                  transition={{ duration: 0.3, delay: 0.6, type: 'spring' }}
                  className="text-[#7CD4FD] ml-1"
                >
                  -AI
                </motion.span>
              </div>

              {/* Tagline */}
              <motion.p
                className="mt-3 text-lg tracking-wider"
                initial={{ opacity: 0 }}
                animate={{ opacity: phase >= 1 ? 0.7 : 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                style={{ color: '#94A3B8' }}
              >
                KI-gestützte Fahrzeugdiagnose
              </motion.p>
            </motion.div>

            {/* Loading Indicator */}
            <motion.div
              className="mt-8 flex items-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: phase >= 1 && phase < 3 ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: '#7CD4FD' }}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 0.6,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </motion.div>
          </div>

          {/* Radial Lines Effect */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            animate={{
              opacity: phase >= 2 ? [0.3, 0] : 0.1,
            }}
            transition={{ duration: 0.5 }}
          >
            {[...Array(12)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute left-1/2 top-1/2 h-[200vh] w-px origin-top"
                style={{
                  background: 'linear-gradient(to bottom, rgba(124, 212, 253, 0.3), transparent)',
                  transform: `rotate(${i * 30}deg)`,
                }}
                animate={{
                  scaleY: phase >= 2 ? [1, 2] : 1,
                  opacity: phase >= 2 ? [0.3, 0] : 0.1,
                }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.02,
                }}
              />
            ))}
          </motion.div>

          {/* Flash Effect on Exit */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundColor: '#7CD4FD' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 2 ? [0, 0.3, 0] : 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          />

          {/* CSS for grid animation */}
          <style>{`
            @keyframes gridMove {
              0% { transform: translate(0, 0); }
              100% { transform: translate(50px, 50px); }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashPreloader;
